import React, { useState, useEffect } from "react";
import Section from "./layouts/Section";
import { SiteWide, CountrySelector } from "./customComponents";
import { useSelector, useDispatch } from 'react-redux';

import { authActions } from '../redux/actions';
import { userSelect, allRemoteSettingsSelect, homeScreenStatSelect } from '../redux/selectors/index';
import { notify } from '../utility/Helper1';
import { staticCountries } from '../utility/countries';

// MUI icons
import PhoneIcon from "@mui/icons-material/Phone";
import PublicIcon from "@mui/icons-material/Public";
import LockIcon from "@mui/icons-material/Lock";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

function PhoneComponent() {
    const userData = useSelector(userSelect);
    const dispatch = useDispatch();

    const [loading, setLoading] = useState(false);
    const [loadingText, setLoadingText] = useState('Updating phone number...');

    const [phone, setPhone] = useState(userData?.phone ?? '');
    const [country, setCountry] = useState(userData?.country ?? '');
    const [phoneCode, setPhoneCode] = useState('');
    const [withdrawalPin, setWithdrawalPin] = useState('');
    const [showPin, setShowPin] = useState(false);
    const [countryFocused, setCountryFocused] = useState(false);

    const handlePhoneChange = async (e) => {
        e.preventDefault();
        if (!phone || !country) { notify('Please fill in all phone fields', 0); return; }
        if (!withdrawalPin) { notify('Please enter your PIN', 0); return; }

        setLoading(true);
        dispatch(authActions.onLogin("account/update_phone", { country, phone, withdrawalPin }, response => {
            if (response.code === 1) { setWithdrawalPin(''); }
            notify(response.msg, response.code);
            setLoading(false);
        }));
    };

    const handleCountryChange = (countryId) => {
        const selectedCountry = staticCountries.find(e => e.id == countryId);
        setCountry(countryId);
        setPhoneCode(selectedCountry?.phonecode ? `+${selectedCountry.phonecode}` : '');
    };

    useEffect(() => { handleCountryChange(country); }, []);

    // ── shared styles ──────────────────────────────────────────────────────────
    const inputStyle = {
        width: "100%", borderRadius: 12, padding: "13px 14px",
        border: "1px solid rgba(59,130,246,0.15)",
        background: "#f8fafc", color: "#0f172a",
        fontSize: 14, fontFamily: "'DM Sans',sans-serif",
        outline: "none", boxSizing: "border-box",
        transition: "border 0.2s"
    };

    const labelStyle = {
        color: "#334155", fontSize: 12, fontWeight: 600,
        fontFamily: "'DM Sans',sans-serif",
        display: "block", marginBottom: 6
    };

    const tips = [
        { text: "Use the full number without country code prefix" },
        { text: "Make sure your phone number is active and accessible" },
        { text: "A valid phone helps recover your account" },
    ];

    return (
        <SiteWide id="main" title="Update Phone Number">
            <Section loading={loading} overlayText={loadingText} title="Update Phone Number">
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

                    {/* ── Header card ── */}
                    <div style={{
                        borderRadius: 16, padding: 20, position: "relative", overflow: "hidden",
                        background: "linear-gradient(135deg,#064e3b 0%,#059669 50%,#10b981 100%)",
                        border: "1px solid rgba(52,211,153,0.2)"
                    }}>
                        <div style={{
                            position: "absolute", top: 0, right: 0, opacity: 0.12,
                            width: 140, height: 140,
                            background: "radial-gradient(circle,#6ee7b7,transparent)",
                            borderRadius: "50%", transform: "translate(30px,-30px)"
                        }} />
                        <div style={{ display: "flex", alignItems: "center", gap: 14, position: "relative" }}>
                            <div style={{
                                width: 48, height: 48, borderRadius: 14,
                                background: "rgba(255,255,255,0.15)",
                                backdropFilter: "blur(8px)",
                                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0
                            }}>
                                <PhoneIcon style={{ color: "#ffffff", fontSize: 24 }} />
                            </div>
                            <div>
                                <p style={{ color: "#ffffff", fontSize: 16, fontWeight: 800, fontFamily: "'Syne',sans-serif", margin: 0 }}>
                                    Update Phone Number
                                </p>
                                <p style={{ color: "rgba(209,250,229,0.7)", fontSize: 12, fontFamily: "'DM Sans',sans-serif", margin: 0 }}>
                                    Keep your contact information up to date
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* ── Current phone info ── */}
                    {userData?.phone && (
                        <div style={{
                            background: "#ffffff", border: "1px solid rgba(59,130,246,0.08)",
                            borderRadius: 14, padding: "12px 16px",
                            display: "flex", alignItems: "center", gap: 12
                        }}>
                            <div style={{
                                width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                                background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.15)",
                                display: "flex", alignItems: "center", justifyContent: "center"
                            }}>
                                <PhoneIcon style={{ color: "#22c55e", fontSize: 18 }} />
                            </div>
                            <div>
                                <p style={{ color: "#94a3b8", fontSize: 10, fontFamily: "'DM Sans',sans-serif", margin: 0, textTransform: "uppercase", letterSpacing: "0.04em" }}>Current Phone</p>
                                <p style={{ color: "#0f172a", fontSize: 13, fontWeight: 600, fontFamily: "'DM Sans',sans-serif", margin: 0 }}>{userData.phone}</p>
                            </div>
                        </div>
                    )}

                    {/* ── Form card ── */}
                    <div style={{
                        background: "#ffffff", border: "1px solid rgba(59,130,246,0.08)",
                        boxShadow: "0 4px 18px rgba(59,130,246,0.04)", borderRadius: 16, padding: 20
                    }}>
                        <form onSubmit={handlePhoneChange} style={{ display: "flex", flexDirection: "column", gap: 16 }}>

                            {/* Country */}
                            <div>
                                <label style={labelStyle}>Country</label>
                                <CountrySelector
                                    value={country}
                                    onChange={(countryId) => handleCountryChange(countryId)}
                                    onFocus={() => setCountryFocused(true)}
                                    onBlur={() => setCountryFocused(false)}
                                    focused={countryFocused}
                                />
                            </div>

                            {/* Phone number */}
                            <div>
                                <label style={labelStyle}>Phone Number</label>
                                <div style={{ position: "relative", display: "flex", gap: 0 }}>
                                    {/* Country code badge */}
                                    {phoneCode && (
                                        <div style={{
                                            borderRadius: "12px 0 0 12px", padding: "13px 12px",
                                            background: "rgba(59,130,246,0.06)",
                                            border: "1px solid rgba(59,130,246,0.15)",
                                            borderRight: "none",
                                            color: "#2563eb", fontSize: 13,
                                            fontWeight: 700, fontFamily: "'Syne',sans-serif",
                                            whiteSpace: "nowrap", display: "flex", alignItems: "center"
                                        }}>
                                            {phoneCode}
                                        </div>
                                    )}
                                    <input
                                        type="text"
                                        value={phone}
                                        onChange={(e) => setPhone(e.target.value)}
                                        placeholder="XXX XXX XXXX"
                                        style={{
                                            ...inputStyle,
                                            flex: 1,
                                            borderRadius: phoneCode ? "0 12px 12px 0" : 12,
                                        }}
                                    />
                                </div>
                                <p style={{ color: "#94a3b8", fontSize: 11, fontFamily: "'DM Sans',sans-serif", margin: "5px 0 0" }}>
                                    Enter your number without the country code
                                </p>
                            </div>

                            {/* PIN */}
                            <div>
                                <label style={labelStyle}>PIN Verification</label>
                                <div style={{ position: "relative" }}>
                                    <input
                                        type={showPin ? 'text' : 'password'}
                                        value={withdrawalPin}
                                        onChange={(e) => setWithdrawalPin(e.target.value)}
                                        maxLength={4}
                                        placeholder="4-digit withdrawal PIN"
                                        style={{ ...inputStyle, paddingRight: 44 }}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPin(!showPin)}
                                        style={{
                                            position: "absolute", right: 12, top: "50%",
                                            transform: "translateY(-50%)", background: "none",
                                            border: "none", cursor: "pointer", color: "#94a3b8",
                                            display: "flex", alignItems: "center", padding: 0
                                        }}
                                    >
                                        {showPin
                                            ? <VisibilityOffIcon style={{ fontSize: 18 }} />
                                            : <VisibilityIcon style={{ fontSize: 18 }} />}
                                    </button>
                                </div>
                                <p style={{ color: "#94a3b8", fontSize: 11, fontFamily: "'DM Sans',sans-serif", margin: "5px 0 0" }}>
                                    Enter your 4-digit PIN to confirm changes
                                </p>
                            </div>

                            {/* Submit */}
                            <button
                                type="submit"
                                disabled={loading}
                                style={{
                                    width: "100%", borderRadius: 12, padding: "13px 0", border: "none",
                                    background: loading
                                        ? "rgba(16,185,129,0.3)"
                                        : "linear-gradient(135deg,#10b981,#059669)",
                                    color: "#fff", fontSize: 14, fontWeight: 700,
                                    fontFamily: "'Syne',sans-serif", cursor: loading ? "not-allowed" : "pointer",
                                    boxShadow: loading ? "none" : "0 4px 16px rgba(16,185,129,0.25)",
                                    marginTop: 4
                                }}
                            >
                                {loading ? 'Updating...' : 'Update Phone Number'}
                            </button>
                        </form>
                    </div>

                    {/* ── Tips ── */}
                    <div style={{
                        background: "#ffffff", border: "1px solid rgba(59,130,246,0.08)",
                        boxShadow: "0 2px 12px rgba(59,130,246,0.02)", borderRadius: 16, padding: 20
                    }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                            <div style={{
                                width: 36, height: 36, borderRadius: 10,
                                background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.15)",
                                display: "flex", alignItems: "center", justifyContent: "center"
                            }}>
                                <InfoOutlinedIcon style={{ color: "#10b981", fontSize: 18 }} />
                            </div>
                            <p style={{ color: "#0f172a", fontSize: 13, fontWeight: 700, fontFamily: "'Syne',sans-serif", margin: 0 }}>
                                Helpful Tips
                            </p>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                            {tips.map((tip, i) => (
                                <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                                    <CheckCircleIcon style={{ color: "#22c55e", fontSize: 15, marginTop: 1, flexShrink: 0 }} />
                                    <p style={{ color: "#475569", fontSize: 12, fontFamily: "'DM Sans',sans-serif", margin: 0, lineHeight: 1.5 }}>
                                        {tip.text}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>

                </div>
            </Section>
        </SiteWide>
    );
}

export default PhoneComponent;